/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import { GridTabs } from '@src/modules/unused.access/models'
import {
  UnusedIdentityType,
  GraphQueryMgr,
  UnusedAccessReq,
  TopLevelCountForUnusedAccess,
  UnusedAccessResp
} from '@src/endpoints/services/graphQueryMgr.service'
import type * as Models from './models'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { parseItems } from './helpers'
import type { State } from '@src/redux/configureStore'
import { isEmpty } from 'lodash'

export const initialState = {
  isPending: false as boolean,
  identity_type: UnusedIdentityType.UNUSED_ROLE,
  tab: GridTabs.UnusedRoles,
  //by role
  unusedRoles: [] as Models.UnusedRole[],
  unusedRolesByServices: [] as Models.UnusedRoleByServices[],
  unusedRolesByPrivileges: [] as Models.UnusedRoleByPrivilege[],
  //by Identity
  unusedIdentities: [] as Models.UnusedIdentity[],
  unusedIdentitiesByServices: [] as Models.UnusedIdentityByServices[],
  unusedIdentitiesByPrivileges: [] as Models.UnusedIdentityByPrivileges[],
  // by Identity without role
  unusedIdentitiesWithoutRole: [] as Models.UnusedIdentityWithoutRole[],
  unusedIdentitiesWithoutRoleByServices:
    [] as Models.UnusedIdentityWithoutRoleByServices[],
  unusedIdentitiesWithoutRoleByPrivileges:
    [] as Models.UnusedIdentityWithoutRoleByPrivileges[],

  topTabCounters: {} as TopLevelCountForUnusedAccess,
  usageOnboardingInProgress: [] as string[]
}

interface PayloadData {
  unusedRoles?: Models.UnusedRole[]
  unusedRolesByServices?: Models.UnusedRoleByServices[]
  unusedRolesByPrivileges?: Models.UnusedRoleByPrivilege[]
  unusedIdentities?: Models.UnusedIdentity[]
  unusedIdentitiesByServices?: Models.UnusedIdentityByServices[]
  unusedIdentitiesByPrivileges?: Models.UnusedIdentityByPrivileges[]
  unusedIdentitiesWithoutRole?: Models.UnusedIdentityWithoutRole[]
  unusedIdentitiesWithoutRoleByServices?: Models.UnusedIdentityWithoutRoleByServices[]
  unusedIdentitiesWithoutRoleByPrivileges?: Models.UnusedIdentityWithoutRoleByPrivileges[]
  topTabCounters?: TopLevelCountForUnusedAccess
  usageOnboardingInProgress?: string[]
}

export interface GetDataPayload extends PayloadData {
  isPending?: boolean
}

const unusedAccess = createSlice({
  name: 'unusedAccess',
  initialState,
  reducers: {
    changeState: (
      state,
      action: PayloadAction<Partial<typeof initialState>>
    ) => ({ ...state, ...action.payload }),
    changeTabs: (
      state,
      action: PayloadAction<{
        identity_type?: UnusedIdentityType
        tab?: GridTabs
      }>
    ) => {
      state.identity_type =
        action.payload.identity_type || initialState.identity_type
      state.tab = action.payload.tab || initialState.tab
    },
    pending: (state, action: PayloadAction<{ isPending?: boolean }>) => {
      state.isPending = action.payload.isPending
    },
    selectTab: (state, action: PayloadAction<GridTabs>) => {
      state.tab = action.payload
    },
    selectIdentityType: (state, action: PayloadAction<UnusedIdentityType>) => {
      state.identity_type = action.payload
      state.tab = initialState.tab
      state.topTabCounters = {}
    },
    loadedData: (state, action: PayloadAction<GetDataPayload>) => ({
      ...state,
      ...action.payload
    }),
    dispose: state => initialState
  }
})

export const fetchData =
  (params: UnusedAccessReq) => (dispatch: any, getStore: () => State) => {
    const { auth, filter } = getStore()
    dispatch(unusedAccess.actions.pending({ isPending: true }))
    GraphQueryMgr.UnusedAccess({ params: paramsWithFilter(params, filter) })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(unusedAccess.actions.pending({ isPending: false }))
          },
          true
        )
      )
      .then(response => {
        const {
          roles,
          roles_by_service,
          roles_by_privilege,
          identities,
          identities_by_service,
          identities_by_privilege,
          identities_without_role,
          identities_without_role_by_privilege,
          identities_without_role_by_service,
          top_level_count_for_unused_access: topCounts,
          usage_onboarding_in_progress
        } = response || ({} as UnusedAccessResp)

        const data: GetDataPayload = {}
        if (!isEmpty(topCounts)) {
          data.topTabCounters = topCounts
        }

        if (!isEmpty(roles)) {
          data.unusedRoles = parseItems(roles?.items, auth)
        }

        if (!isEmpty(roles_by_service)) {
          data.unusedRolesByServices = parseItems(roles_by_service?.items, auth)
        }
        if (!isEmpty(roles_by_privilege)) {
          data.unusedRolesByPrivileges = parseItems(
            roles_by_privilege?.items,
            auth
          )
        }
        if (!isEmpty(identities)) {
          data.unusedIdentities = parseItems(identities?.items, auth)
        }
        if (!isEmpty(identities_by_service)) {
          data.unusedIdentitiesByServices = parseItems(
            identities_by_service?.items,
            auth
          )
        }
        if (!isEmpty(identities_by_privilege)) {
          data.unusedIdentitiesByPrivileges = parseItems(
            identities_by_privilege?.items,
            auth
          )
        }

        if (!isEmpty(identities_without_role)) {
          data.unusedIdentitiesWithoutRole = parseItems(
            identities_without_role?.items,
            auth
          )
        }
        if (!isEmpty(identities_without_role_by_service)) {
          data.unusedIdentitiesWithoutRoleByServices = parseItems(
            identities_without_role_by_service?.items,
            auth
          )
        }
        if (!isEmpty(identities_without_role_by_privilege)) {
          data.unusedIdentitiesWithoutRoleByPrivileges = parseItems(
            identities_without_role_by_privilege?.items,
            auth
          )
        }

        if (!isEmpty(usage_onboarding_in_progress)) {
          data.usageOnboardingInProgress = usage_onboarding_in_progress
        }

        dispatch(
          unusedAccess.actions.loadedData({
            ...data,
            isPending: false
          })
        )
      })
      .catch((res: Response) => {
        dispatch(unusedAccess.actions.pending({ isPending: false }))
      })
  }

export const { reducer, actions, caseReducers } = unusedAccess

export default unusedAccess
